import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Building2, 
  Settings, 
  BarChart3, 
  Shield, 
  Activity,
  AlertTriangle,
  Database
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface AdminCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  color: string;
}

const AdminCard: React.FC<AdminCardProps> = ({ title, description, icon: Icon, href, color }) => {
  return (
    <Link 
      to={href}
      className="block p-6 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
    </Link>
  );
};

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation(['admin_common']);
  const { user } = useAuth();

  if (user?.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">SuperAdmin access required</p>
        </div>
      </div>
    );
  }

  const adminSections = [
    {
      title: t('admin_common:sections.users.title'),
      description: t('admin_common:sections.users.description'), 
      icon: Users,
      href: '/admin/users',
      color: 'bg-blue-500'
    },
    {
      title: t('admin_common:sections.orgs.title'),
      description: t('admin_common:sections.orgs.description'),
      icon: Building2,
      href: '/admin/orgs', 
      color: 'bg-green-500'
    },
    {
      title: t('admin_common:sections.devices.title'),
      description: t('admin_common:sections.devices.description'),
      icon: Activity,
      href: '/admin/devices',
      color: 'bg-purple-500'
    },
    {
      title: t('admin_common:sections.system.title'),
      description: t('admin_common:sections.system.description'),
      icon: Database,
      href: '/admin/system',
      color: 'bg-orange-500'
    },
    {
      title: t('admin_common:sections.analytics.title'),
      description: t('admin_common:sections.analytics.description'),
      icon: BarChart3,
      href: '/admin/analytics',
      color: 'bg-indigo-500'
    },
    {
      title: t('admin_common:sections.settings.title'), 
      description: t('admin_common:sections.settings.description'),
      icon: Settings,
      href: '/admin/settings',
      color: 'bg-gray-500'
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('admin_common:title')}
        </h1>
        <p className="text-gray-600">
          {t('admin_common:subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminSections.map((section, index) => (
          <AdminCard key={index} {...section} />
        ))}
      </div>

      {/* Quick Stats */}
      <div className="mt-12 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {t('admin_common:quickStats.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">--</div>
            <div className="text-sm text-gray-600">{t('admin_common:quickStats.totalUsers')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">--</div>
            <div className="text-sm text-gray-600">{t('admin_common:quickStats.activeOrgs')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">--</div>
            <div className="text-sm text-gray-600">{t('admin_common:quickStats.devices')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">--</div>
            <div className="text-sm text-gray-600">{t('admin_common:quickStats.alerts')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;